import { AvatarDropdown, AvatarName, Footer, Question, SelectLang } from '@/components';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig, RequestConfig} from '@umijs/max';
import { history, Link } from '@umijs/max';
import React from 'react';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import { channelGameCache } from '@/utils/channelGameCache';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */

// 新增安全访问方法
const getSafeLocalStorage = (key: string, defaultValue: string = 'guest') => {
  try {
    return localStorage.getItem(key) || defaultValue;
  } catch (error) {
    console.error('localStorage访问异常:', error);
    return defaultValue;
  }
};

export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  // 迁移缓存数据，删除冗余字段
  channelGameCache.migrateCache();
  
  // console.log('getInitialState');
  // 添加Firebase状态监听
  const checkFirebaseAuth = async () => {
    const { auth } = await import('@/services/firebase');
    return new Promise((resolve) => {
      const unsubscribe = auth.onAuthStateChanged(async (user) => {
        if (user) {
          const tokenResult = await user.getIdTokenResult();
          console.log('Firebase登录状态有效，过期时间:', tokenResult.expirationTime);
          resolve({
            firebaseUser: user,
            tokenExpire: tokenResult.expirationTime
          });
        } else {
          resolve(null);
        }
        unsubscribe(); // 单次检查后取消监听
      });
    });
  };


  const fetchUserInfo = async () => {
    try {
      const firebaseAuth = await checkFirebaseAuth() as {
        firebaseUser: import('firebase/auth').User;
        tokenExpire: string;
      };
      
      if (!firebaseAuth) {
        history.push(loginPath);
        return undefined;
      }
  
      // 直接使用Firebase用户信息构建currentUser
      const { firebaseUser } = firebaseAuth;
      return {
        userid: firebaseUser.uid,
        name: firebaseUser.displayName || 'Firebase用户',
        avatar: firebaseUser.photoURL || undefined,
        email: firebaseUser.email,
        // 补充必要字段（根据API.CurrentUser类型）
        signature: '',
        title: '第三方登录用户',
        group: '',
        tags: [],
        access: getSafeLocalStorage('userAccess', 'user'), // 修改这里
        unreadCount: 0,
        // 保留过期时间
        firebaseExpiration: firebaseAuth.tokenExpire
      } as API.CurrentUser;
  
    } catch (error) {
      console.error('认证状态检查失败', error);
      history.push(loginPath);
      return undefined;
    }
  };
  
  // 如果不是登录页面，执行
  const { location } = history;
  if (location.pathname !== loginPath) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  return {
    actionsRender: () => [<Question key="doc" />, <SelectLang key="SelectLang" />],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // console.log('location.pathname', location.pathname);
      // console.log('initialState', initialState);
      // console.log('initialState?.currentUser', initialState?.currentUser);
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {

  timeout: 100000,
  headers: { 'X-Requested-With': 'XMLHttpRequest' },
  
  // 统一请求拦截
  requestInterceptors: [
    (url: string, options: any) => {
      const token = localStorage.getItem('access_token');
      if (token) {
        options.headers = {
          ...options.headers,
          Authorization: `Bearer ${token}`,
        };
      }
      return { url, options };
    },
  ],
  
  // 统一错误处理
  errorConfig: {
    errorHandler(error: any) {
      if (error.response?.status === 401) {
        localStorage.removeItem('access_token');
        window.location.href = '/user/login';
      }
      throw error;
    }
  },
  // 其他全局配置...
};
